/**
 * Shared per-user column visibility and order preferences for any `DataTable` consumer.
 * Persists to `localStorage` under keys namespaced by `storageKeyPrefix`, so two consumers
 * (e.g. the catalog and the offer units tab) with different prefixes never cross-read/write.
 */
import * as React from 'react';
import { DATA_TABLE_MIN_COLUMN_WIDTH_PX } from './data-table-hooks.js';

export interface UseColumnPreferencesOptions {
  /** Namespaces the localStorage keys: `${storageKeyPrefix}:hiddenColumns` / `:columnOrder`. */
  storageKeyPrefix: string;
  /** All column ids this table can show, including pinned ones. */
  allColumnIds: string[];
  /** Hidden-by-default column ids used only when no `hiddenColumns` key exists yet. */
  defaultHiddenIds?: ReadonlySet<string>;
  /** Column ids that always render first, in this order, and are never toggleable/orderable. */
  pinnedIds?: string[];
}

export interface UseColumnPreferencesResult {
  /** Ids currently visible, including pinned ids. Unordered — pair with `columnOrder` to render. */
  visibleColumnIds: ReadonlySet<string>;
  /** User-hidden toggleable ids. Never contains a pinned id. */
  hiddenColumnIds: ReadonlySet<string>;
  toggleColumn: (id: string) => void;
  /** Pinned ids first (in the given order), then the persisted/derived toggleable order. */
  columnOrder: string[];
  reorderColumn: (draggedId: string, targetId: string) => void;
  moveColumnUp: (id: string) => void;
  moveColumnDown: (id: string) => void;
  /** Persisted per-column pixel widths, keyed by column id. Missing id = fall back to the column's static `width`. */
  columnWidths: Readonly<Record<string, number>>;
  /**
   * Persist a single column's resized width. Ignored (no state change, no write) for an id
   * outside `allColumnIds` or a width below `DataTable`'s 60px resize floor — wire this
   * directly to `DataTable`'s `onColumnResizeEnd` prop.
   */
  setColumnWidth: (id: string, width: number) => void;
}

const EMPTY_HIDDEN_IDS: ReadonlySet<string> = new Set();

function isValidStoredWidth(value: unknown): value is number {
  return (
    typeof value === 'number' && Number.isFinite(value) && value >= DATA_TABLE_MIN_COLUMN_WIDTH_PX
  );
}

function readWidthsFromStorage(key: string, allColumnIds: string[]): Record<string, number> {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return {};
    const parsed: unknown = JSON.parse(stored);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    const result: Record<string, number> = {};
    for (const [id, width] of Object.entries(parsed as Record<string, unknown>)) {
      if (!allColumnIds.includes(id)) continue;
      if (!isValidStoredWidth(width)) continue;
      result[id] = width;
    }
    return result;
  } catch {
    return {};
  }
}

function saveWidths(key: string, next: Record<string, number>) {
  try {
    localStorage.setItem(key, JSON.stringify(next));
  } catch {
    // localStorage may be unavailable; silently ignore
  }
}

function readHiddenFromStorage(key: string, defaultHiddenIds: ReadonlySet<string>): Set<string> {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return new Set(defaultHiddenIds);
    const parsed: unknown = JSON.parse(stored);
    if (!Array.isArray(parsed)) return new Set<string>();
    return new Set(parsed.filter((v): v is string => typeof v === 'string'));
  } catch {
    return new Set<string>();
  }
}

function saveHidden(key: string, next: Set<string>) {
  try {
    localStorage.setItem(key, JSON.stringify([...next]));
  } catch {
    // localStorage may be unavailable; silently ignore
  }
}

function readOrderFromStorage(key: string, toggleableIds: string[]): string[] {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return [...toggleableIds];
    const parsed: unknown = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [...toggleableIds];
    // Ids no longer toggleable (removed columns, or stale pinned ids from a previous
    // session) are dropped here rather than kept mid-list — pinned ids are re-added
    // by the caller as a leading prefix, never from persisted order.
    const validIds = parsed.filter(
      (v): v is string => typeof v === 'string' && toggleableIds.includes(v),
    );
    const missing = toggleableIds.filter((id) => !validIds.includes(id));
    return [...validIds, ...missing];
  } catch {
    return [...toggleableIds];
  }
}

function saveOrder(key: string, next: string[]) {
  try {
    localStorage.setItem(key, JSON.stringify(next));
  } catch {
    // localStorage may be unavailable; silently ignore
  }
}

function useHiddenColumns(
  hiddenStorageKey: string,
  defaultHiddenIds: ReadonlySet<string>,
  toggleableIds: string[],
) {
  const [hiddenColumnIds, setHiddenColumnIds] = React.useState<Set<string>>(() =>
    readHiddenFromStorage(hiddenStorageKey, defaultHiddenIds),
  );

  const toggleColumn = React.useCallback(
    (id: string) => {
      const next = new Set(hiddenColumnIds);
      if (next.has(id)) {
        next.delete(id);
      } else if (toggleableIds.includes(id)) {
        next.add(id);
      }
      setHiddenColumnIds(next);
      saveHidden(hiddenStorageKey, next);
    },
    [hiddenColumnIds, toggleableIds, hiddenStorageKey],
  );

  return { hiddenColumnIds, toggleColumn };
}

function useColumnWidths(widthsStorageKey: string, allColumnIds: string[]) {
  const [columnWidths, setColumnWidths] = React.useState<Record<string, number>>(() =>
    readWidthsFromStorage(widthsStorageKey, allColumnIds),
  );

  const setColumnWidth = React.useCallback(
    (id: string, width: number) => {
      if (!allColumnIds.includes(id) || !isValidStoredWidth(width)) return;
      setColumnWidths((prev) => {
        const next = { ...prev, [id]: width };
        saveWidths(widthsStorageKey, next);
        return next;
      });
    },
    [allColumnIds, widthsStorageKey],
  );

  return { columnWidths, setColumnWidth };
}

function useToggleableOrder(orderStorageKey: string, toggleableIds: string[]) {
  const [order, setOrder] = React.useState<string[]>(() =>
    readOrderFromStorage(orderStorageKey, toggleableIds),
  );

  const persistOrderAndSet = React.useCallback(
    (next: string[]) => {
      setOrder(next);
      saveOrder(orderStorageKey, next);
    },
    [orderStorageKey],
  );

  const reorderColumn = React.useCallback(
    (draggedId: string, targetId: string) => {
      if (draggedId === targetId) return;
      const fromIdx = order.indexOf(draggedId);
      const toIdx = order.indexOf(targetId);
      if (fromIdx < 0 || toIdx < 0) return;
      const next = [...order];
      next.splice(fromIdx, 1);
      // The drop indicator lands the column where the insert line is drawn:
      // after the target on a forward (left->right) drag, before it on a backward
      // drag. After removing the dragged column, both cases resolve to `toIdx`:
      // forward shifts the target left to toIdx-1, so +1 lands after it; backward
      // leaves the target at toIdx, so inserting at toIdx lands before it.
      next.splice(toIdx, 0, draggedId);
      persistOrderAndSet(next);
    },
    [order, persistOrderAndSet],
  );

  const moveColumnUp = React.useCallback(
    (id: string) => {
      const idx = order.indexOf(id);
      if (idx <= 0) return;
      reorderColumn(id, order[idx - 1]!);
    },
    [order, reorderColumn],
  );

  const moveColumnDown = React.useCallback(
    (id: string) => {
      const idx = order.indexOf(id);
      if (idx < 0 || idx >= order.length - 1) return;
      reorderColumn(order[idx + 1]!, id);
    },
    [order, reorderColumn],
  );

  return { order, reorderColumn, moveColumnUp, moveColumnDown };
}

export function useColumnPreferences({
  storageKeyPrefix,
  allColumnIds,
  defaultHiddenIds = EMPTY_HIDDEN_IDS,
  pinnedIds = [],
}: UseColumnPreferencesOptions): UseColumnPreferencesResult {
  const hiddenStorageKey = `${storageKeyPrefix}:hiddenColumns`;
  const orderStorageKey = `${storageKeyPrefix}:columnOrder`;
  const widthsStorageKey = `${storageKeyPrefix}:columnWidths`;
  const toggleableIds = allColumnIds.filter((id) => !pinnedIds.includes(id));

  const { hiddenColumnIds, toggleColumn } = useHiddenColumns(
    hiddenStorageKey,
    defaultHiddenIds,
    toggleableIds,
  );

  const { columnWidths, setColumnWidth } = useColumnWidths(widthsStorageKey, allColumnIds);

  const { order, reorderColumn, moveColumnUp, moveColumnDown } = useToggleableOrder(
    orderStorageKey,
    toggleableIds,
  );

  const columnOrder = React.useMemo(() => [...pinnedIds, ...order], [pinnedIds, order]);

  const visibleColumnIds = React.useMemo(() => {
    const visible = new Set<string>(pinnedIds);
    for (const id of toggleableIds) {
      if (!hiddenColumnIds.has(id)) visible.add(id);
    }
    return visible;
  }, [pinnedIds, toggleableIds, hiddenColumnIds]);

  return {
    visibleColumnIds,
    hiddenColumnIds,
    toggleColumn,
    columnOrder,
    reorderColumn,
    moveColumnUp,
    moveColumnDown,
    columnWidths,
    setColumnWidth,
  };
}
